// DS: CO0.2, CO0.2M
import React from 'react';
import { PapiDeviation, RelatedBlock } from '@wix/da-papi-types';
import DeviationGrid3x3 from './layouts/DeviationGrid3X3';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import SingleColumnTextual from './layouts/SingleColumnTextual';
import DeviationMosaic from './layouts/DeviationMosaic';
import TierList from './layouts/TierList';
import DreamupPromo from './layouts/DreamupPromo';
import DeviationMobileList from './layouts/DeviationMobileList';
import Groups from './layouts/Groups';
import Collections from './layouts/Collections';
import Dealer from './layouts/Dealer';
import MonetizationBlock from './layouts/MonetizationBlock';
import AdoptableInfo from './layouts/AdoptableInfo';
import PcpInfo from './layouts/PcpInfo';
import Users from './layouts/Users';
import Topics from './layouts/Topics';
import ShopPromo from './layouts/ShopPromo';
import BoostPromo from './layouts/BoostPromo';
import LatestPost from './layouts/LatestPost';
import OriginalInfo from './layouts/OriginalInfo';
import MoreOriginals from './layouts/MoreOriginals';
import OriginalSource from './layouts/OriginalSource';

interface Props {
  block: RelatedBlock;
  deviation: PapiDeviation;
  className?: string;
}

const genericLayouts: { [k: number]: React.ComponentType<Props> } = {
  1: DeviationGrid3x3,
  2: SingleColumnTextual,
  3: DeviationMosaic,
  4: TierList,
  5: DeviationMobileList,
};

const uniqueLayouts: { [k: string]: React.ComponentType<Props> } = {
  subscription_promotion: MonetizationBlock, // backwards compatibility
  monetization_promotion: MonetizationBlock,
  monetization_promotion_tier: MonetizationBlock,
  monetization_promotion_tier_firstparty: MonetizationBlock,
  monetization_promotion_tier_generic: MonetizationBlock,
  monetization_promotion_tier_thirdparty: MonetizationBlock,
  monetization_promotion_commission: MonetizationBlock,
  monetization_promotion_commission_generic: MonetizationBlock,
  monetization_promotion_premium_gallery: MonetizationBlock,
  monetization_promotion_premium_gallery_generic: MonetizationBlock,
  monetization_promotion_pcp: MonetizationBlock,
  monetization_promotion_pcp_generic: MonetizationBlock,
  monetization_promotion_pcp_firstparty: MonetizationBlock,
  monetization_promotion_pcp_thirdparty: MonetizationBlock,
  monetization_promotion_adoptable: MonetizationBlock,
  monetization_promotion_adoptable_firstparty: MonetizationBlock,
  monetization_promotion_adoptable_thirdparty: MonetizationBlock,
  monetization_promotion_adoptable_generic: MonetizationBlock,
  monetization_promotion_premium_gallery_firstparty: MonetizationBlock,
  monetization_promotion_premium_gallery_thirdparty: MonetizationBlock,
  monetization_promotion_core_generic: MonetizationBlock,
  monetization_promotion_dreamup_firstparty: DreamupPromo,
  monetization_promotion_dreamup_thirdparty: DreamupPromo,
  monetization_promotion_originals_firstparty: OriginalSource,
  list_this_deviation: ShopPromo,
  boost_promotion: BoostPromo,
  adoptable_info: AdoptableInfo,
  pcp_info: PcpInfo,
  originals_info: OriginalInfo,
  groups: Groups,
  collections: Collections,
  dealer: Dealer,
  users: Users,
  topics: Topics,
  latest_post: LatestPost,
  other_originals: MoreOriginals,
};

function getBlockComponent(block: RelatedBlock) {
  if (block.layout === 0) {
    return uniqueLayouts[block.contentType];
  }

  return genericLayouts[block.layout];
}

const SidebarBlock: React.FC<Props> = props => {
  const BlockComponent = getBlockComponent(props.block);

  if (BlockComponent) {
    return (
      <BiLoggerContextProvider value={{ sectionname: props.block.contentType }}>
        <BlockComponent {...props} />
      </BiLoggerContextProvider>
    );
  }

  return null;
};
SidebarBlock.displayName = 'SidebarBlock';

export default SidebarBlock;
